import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { Credentials } from "../types";
import { AccountHttpService } from "../generated/account-http.service";

export type Role = 'User' | 'Full' | 'Consultant' | 'Admin';

/** Ein Objekt, dass zu einem Form Data String serialisiert werden kann. */
export type FormDataStringSerializable =
  | undefined
  | null
  | boolean
  | number
  | string
  | { [key: string]: FormDataStringSerializable };

export const toFormDataString = (data: FormDataStringSerializable, prefix = ''): string => {

  if (data == null) {
    return prefix;
  }

  if (typeof data === 'boolean' || typeof data === 'number' || typeof data === 'string') {
    return (prefix.length === 0 ? '' : prefix + '=') + encodeURIComponent(data.toString());
  }

  return Object.keys(data)
    .filter(key => data[key] != null)
    .map((key) => toFormDataString(data[key], prefix.length === 0 ? key : prefix + '.' + key))
    .join('&');
}

interface AuthInfoResponse {
  '.expires': string;
  '.issued': string;
  access_token: string;
  expires_in: number;
  token_type: 'bearer';
  userName: string;
  userId: string;
  roles: string;
  imageId: string;
  isCertify: string
}

interface AuthInfo {
  '.expires': string;
  '.issued': string;
  access_token: string;
  expires_in: number;
  token_type: 'bearer';
  userName: string;
  userId: string;
  roles: string[];
  imageId: string;
  isCertify: boolean
}

export function URI(literals: TemplateStringsArray, ...values: (number | boolean | string | { name: string; items: any[] })[]) {
  let result = '';
  const length = Math.max(literals.length, values.length);
  for (let i = 0; i < length; i++) {
    result += literals[i];
    const value = values[i];
    if (value !== undefined) {
      if (typeof value === 'object') {
        result += value.items.map(v => value.name + '=' + encodeURIComponent(v)).join('&');
      } else {
        result += encodeURIComponent(values[i].toString());
      }
    }
  }
  return result;
}

@Injectable()
export class AuthService {

  authInfoBehavior = new BehaviorSubject<AuthInfo>(JSON.parse(sessionStorage.getItem('AUTH')));

  constructor(private httpClient: HttpClient) {

  }

  async logIn({ name, password }: Credentials) {

    const data = { grant_type: 'password', username: name, password };
    const body = toFormDataString(data);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' });
    const authInfoResponse = await this.httpClient.post<AuthInfoResponse>('/token', body, { headers }).toPromise();

    const authInfo: AuthInfo = {
      ...authInfoResponse,
      roles: authInfoResponse.roles.split(';'),
      isCertify: authInfoResponse.isCertify === 'true' ? true : false
    };

    sessionStorage.setItem('AUTH', JSON.stringify(authInfo));
    this.authInfoBehavior.next(authInfo);

    return authInfo;
  }

  logOut() {
    sessionStorage.removeItem('AUTH');
    window.location.reload();
  }

  private isInRole(role: Role) {
    return this.authInfoBehavior.value != null && this.authInfoBehavior.value.roles.includes(role);
  }

  get isFull() {
    return this.isInRole('Full') || this.isInRole('Consultant') || this.isInRole('Admin');
  }

  get isConsultant() {
    return this.isInRole('Consultant') || this.isInRole('Admin');
  }

  get isAdmin() {
    return this.isInRole('Admin');
  }

  get isCertify() {
    return this.authInfoBehavior.value.isCertify;
  }

}
